// [FORENSIC][COMMAND_CONTROLLER][LOAD]
telemetry.forensic('COMMAND_CONTROLLER', 'LOAD', { status: 'BOOT' });

/**
 * Wingman Spatial Command Controller
 * 
 * 🔒 THE ONE TRUE AUTHORITY
 * Inspired by deck.gl's Controller architecture, this module provides a centralized
 * command arbitration layer for all camera movements in Wingman.
 */

import { spatialStateMachine } from './spatialStateMachine';
import { ForensicLogger } from '../observability/ForensicLogger';
import { validateSpatialIntent } from './coordinateValidator';
import { smartCameraMove } from './smartCameraRouter';
import { spatialEventBus } from '@hdri/src/spatialSync/spatialEventBus';
import { WingmanAnalytics } from '../services/analyticsService';
import { WeatherService } from '../services/WeatherService';
import { PoiService } from '../services/PoiService';
import { contextState } from '../context/contextState';
import { voiceState } from '../state/voiceState';
import { speechAuthority } from '../services/voice/SpeechAuthority';
import { telemetry } from './utils/telemetry';
import {
    normalizeCoordinate,
    calculateDistanceHaversine,
    isValidCoordinate
} from './coordinateValidator';
import { shaderGovernance } from './ShaderGovernance';
import { useWingmanStore } from '../store/useWingmanStore';

const toLngLatArray = (input: any) => normalizeCoordinate(input);
const toLngLatObject = (input: any) => {
    const norm = normalizeCoordinate(input);
    return norm ? { lng: norm[0], lat: norm[1] } : null;
};

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type CommandSource = 'USER' | 'VOICE' | 'AI' | 'COPILOT' | 'SYNC' | 'INDOOR_MAP';
export type CommandType = 'FLY_TO' | 'FIT_BOUNDS' | 'JUMP_TO' | 'SYNC_CAMERA';

export interface InteractionState {
    isDragging: boolean;
    isZooming: boolean;
    isAnimating: boolean;
    isPanning: boolean;
    inTransition: boolean;
}

export type MapType = 'background' | 'panel';

/**
 * 🚀 ATOM M-017: Authoritative location state.
 * Written only by the background (Main) map.
 * Read by panel (MiniMap) maps for reconciliation.
 */
export interface SpatialLocation {
    center: { lng: number; lat: number };
    zoom: number;
    pitch: number;
    bearing: number;
}

export interface MapEntry {
    map: mapboxgl.Map;
    type: MapType;
    runtimeId?: string;
}

export interface SpatialCommand {
    id: string;
    type: CommandType;
    source: CommandSource;
    priority: number;
    params: any;
    timestamp: number;
    retryCount?: number;
    correlationId?: string;
}

export interface CommandResult {
    success: boolean;
    reason?: string;
    commandId?: string;
}

// ============================================================================
// COMMAND PRIORITIES (higher = more important)
// ============================================================================

const COMMAND_PRIORITIES: Record<CommandSource | 'USER_DRAGGING' | 'AI_ESCALATED' | 'BACKGROUND' | 'TOUCH', number> = {
    'TOUCH': 6,
    'USER_DRAGGING': 6,
    'USER': 5,
    'VOICE': 5, // 🎙️ Voice navigation matches User priority
    'INDOOR_MAP': 5, // 🛫 Certified Airport Fast Path explicit navigation matches USER/VOICE
    'COPILOT': 4,
    'AI_ESCALATED': 3.5,
    'SYNC': 3,
    'AI': 2,
    'BACKGROUND': 1
};

// ============================================================================
// SPATIAL COMMAND CONTROLLER
// ============================================================================

export class SpatialCommandController {
    // Command queue
    private commandQueue: SpatialCommand[] = [];
    private isProcessingQueue: boolean = false;

    // Interaction state (deck.gl pattern)
    private interactionState: InteractionState = {
        isDragging: false,
        isZooming: false,
        isAnimating: false,
        isPanning: false,
        inTransition: false
    };

    private lastZoomEmission: Record<string, number> = {};

    // Conflict tracking
    private lastFlyToTimestamp: number = 0;
    private lastCommand = { lat: 0, lon: 0, timestamp: 0 };
    private userInteractionTimeout: any = null;
    private safetyAnimationTimeout: any = null;

    // Callbacks
    private onCommandExecuted?: (command: SpatialCommand) => void;
    private onCommandRejected?: (command: SpatialCommand, reason: string) => void;
    private onInteractionStateChanged?: (state: InteractionState) => void;

    // Retry queue for commands blocked by user interaction
    private retryQueue: SpatialCommand[] = [];

    // Map instances for multi-map sync
    private maps: MapEntry[] = [];
    private currentPadding: any = null;

    // 🚀 ATOM M-017: Authoritative location state
    private currentLocation: SpatialLocation | null = null;
    private isLocationLocked: boolean = false;

    constructor() {
        this.initializeInteractionDetection();
        this.initializePaddingSync();
        this.setupListeners();
    }

    /**
     * ATOM S1.5 — Listener Governance
     * Setup or restore authoritative event listeners.
     */
    public setupListeners(): void {
        // 🚀 ATOM D1.4C — AUTHORITATIVE INTENT INTAKE
        if (typeof window === 'undefined') return;

        // Clear existing to prevent duplicates if called multiple times
        spatialEventBus.off('SPATIAL_INTENT', this.handleSpatialIntent);

        spatialEventBus.on('SPATIAL_INTENT', this.handleSpatialIntent);

        // 🚀 ATOM M-01: Global Touch Listeners for Authority Escalation
        window.addEventListener('touchstart', this.handleTouchStart, { passive: true });
        window.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    }

    private handleTouchStart = () => {
        // Elevate to TOUCH priority immediately on mobile interaction
        spatialEventBus.requestLock('TOUCH');
        this.setInteractionState({ isDragging: true });
    };

    private handleTouchEnd = () => {
        // Release lock after a small grace period to allow inertia/easing
        spatialEventBus.releaseLock(800);
        this.setInteractionState({ isDragging: false });
    };

    private handleSpatialIntent = (payload: any) => {
        telemetry.forensic('CONTROLLER', 'INTENT_INTAKE', { source: payload.source || 'AI' });

        // 🚀 ATOM D1.4C: Standardize coordinate mapping for the intake pipeline
        const targetCenter = payload.center || payload.coordinates || payload.payload?.center || payload.payload?.coordinates;

        const intent = {
            intentId: payload.intentId || `intent_${Date.now()}`,
            intentType: payload.intentType || (payload.bounds ? 'FIT_BOUNDS' : 'NAVIGATE'),
            source: payload.source || 'AI',
            payload: {
                ...(payload.payload || payload),
                center: targetCenter
            },
            timestamp: Date.now()
        };

        this.enqueueIntent(intent);
    };

    private traceReadiness() {
        const mapLoaded = this.maps.length > 0 && this.maps.every(m => m.map.isStyleLoaded());
        const orbMounted = !!(window as any).WingmanEventBus;
        const holographicVisible = contextState.getSystemMode() === 'spatial';
        const syncEnabled = !!(window as any).WingmanSpatialControl;
        const cameraAuthority = spatialStateMachine.getAuthority();

        telemetry.debug('COMMAND_CONTROLLER', 'READINESS_TRACE', {
            mapLoaded,
            orbMounted,
            holographicVisible,
            syncEnabled,
            cameraAuthority
        });
    }

    /**
     * Check if a map of a specific type is already registered.
     */
    hasMap(type: MapType): boolean {
        return this.maps.some(entry => entry.type === type);
    }

    /**
     * Register a map instance with the controller.
     * Required for command execution in test environments.
     */
    setMap(map: mapboxgl.Map, type: MapType = 'panel', runtimeId?: string): void {
        const exists = this.maps.some(entry => entry.map === map);
        if (!exists) {
            // 🛡️ AUTHENTIC SINGULARITY: Remove existing map of same type to prevent zombie authority
            this.maps = this.maps.filter(m => m.type !== type);

            telemetry.info('COMMAND_CONTROLLER', 'MAP_REGISTERED', { type, runtimeId });
            this.maps.push({ map, type, runtimeId });

            if (map && typeof map.on === 'function') {
                map.on('moveend', () => {
                    const center = map.getCenter();
                    const zoom = map.getZoom();
                    const bearing = map.getBearing();
                    const pitch = map.getPitch();
                    if (type === 'background') {
                        this.currentLocation = { center, zoom, pitch, bearing };
                        if (typeof (this as any)._syncPanelMaps === 'function') {
                            (this as any)._syncPanelMaps();
                        }
                    }
                });
            }

            // 🚀 ATOM M-017: Immediately reconcile panel maps on registration
            if (type === 'panel') {
                if (this.currentLocation) {
                    try {
                        (map as any).jumpTo(this.currentLocation);
                        telemetry.info('COMMAND_CONTROLLER', 'PANEL_RECONCILED', {
                            center: this.currentLocation.center
                        });
                    } catch (e) {
                        telemetry.warn('COMMAND_CONTROLLER', 'PANEL_RECONCILIATION_FAILED', { e });
                    }
                } else {
                    // No background map yet — use panel map as fallback
                    this.currentLocation = {
                        center: map.getCenter(),
                        zoom: map.getZoom(),
                        pitch: map.getPitch(),
                        bearing: map.getBearing()
                    };
                }
            }

            // Apply current padding to new background maps immediately
            if (type === 'background' && this.currentPadding && typeof map.setPadding === 'function') {
                map.setPadding(this.currentPadding);
            }
        }
    }

    /**
     * ATOM S1.5 — Cold Boot Purge
     * Deterministically clear all authoritative state.
     */
    purgeAll(): void {
        telemetry.warn('COMMAND_CONTROLLER', 'PURGE_ALL_START');

        // 🛡️ ATOM S1.5: Deterministic Reset Chain
        spatialStateMachine.purgeAll();
        if (spatialEventBus) {
            spatialEventBus.purgeAll();
            // 🛡️ ATOM W-010: Force release all locks during purge
            if (typeof (spatialEventBus as any).forceReleaseAllLocks === 'function') {
                (spatialEventBus as any).forceReleaseAllLocks();
            }
        }
        if (shaderGovernance) shaderGovernance.purgeAll();

        // 1. Clear maps
        this.maps = [];
        this.currentLocation = null;

        // 2. Clear timeouts
        if (this.userInteractionTimeout) {
            clearTimeout(this.userInteractionTimeout);
            this.userInteractionTimeout = null;
        }
        if (this.safetyAnimationTimeout) {
            clearTimeout(this.safetyAnimationTimeout);
            this.safetyAnimationTimeout = null;
        }

        // 3. Clear queues
        this.commandQueue = [];
        this.retryQueue = [];
        this.isProcessingQueue = false;
        this.lastCommand = { lat: 0, lon: 0, timestamp: 0 };
        this.lastFlyToTimestamp = 0;
        this.lastZoomEmission = {};

        // 4. Reset state
        this.interactionState = {
            isDragging: false,
            isZooming: false,
            isAnimating: false,
            isPanning: false,
            inTransition: false
        };

        // 🛡️ ATOM W-010: Forensic telemetry after purge
        telemetry.forensic('AUTHORITY', 'PURGE_COMPLETE', {
            interactionState: this.interactionState,
            queueDepth: this.commandQueue.length,
            retryDepth: this.retryQueue.length,
            mapsCount: this.maps.length,
            timestamp: Date.now()
        });

        telemetry.info('COMMAND_CONTROLLER', 'PURGE_ALL_COMPLETE');
    }

    /**
     * 🚀 ATOM M-017: Get the current authoritative location state.
     * Returns null if no background map has reported a position.
     */
    getCurrentLocation(): SpatialLocation | null {
        return this.currentLocation ? { ...this.currentLocation } : null;
    }

    setLocationLock(locked: boolean): void {
        this.isLocationLocked = locked;
        telemetry.info('COMMAND_CONTROLLER', 'LOCATION_LOCK_TOGGLED', { locked });
    }

    getLocationLock(): boolean {
        return this.isLocationLocked;
    }

    /**
     * 🚀 ATOM M-017: Sync all panel maps to the current authoritative location.
     * Called after the background map updates currentLocation via moveend.
     * Panel maps snap (jumpTo) — they do not animate.
     * MiniMap NEVER writes currentLocation. ONLY background map writes.
     */
    private _syncPanelMaps(): void {
        if (!this.currentLocation) return;
        for (const entry of this.maps) {
            if (entry.type === 'panel' && entry.map && typeof (entry.map as any).jumpTo === 'function') {
                try {
                    (entry.map as any).jumpTo(this.currentLocation);
                    telemetry.info('COMMAND_CONTROLLER', 'PANEL_SYNCED', {
                        center: this.currentLocation.center
                    });
                } catch (err) {
                    telemetry.warn('COMMAND_CONTROLLER', 'PANEL_SYNC_FAILED', { err });
                }
            }
        }
    }

    /**
     * Remove a map instance from the controller.
     */
    removeMap(map: mapboxgl.Map): void {
        const idx = this.maps.findIndex(entry => entry.map === map);
        if (idx !== -1) {
            const removed = this.maps[idx];
            this.maps.splice(idx, 1);
            telemetry.info('COMMAND_CONTROLLER', 'MAP_UNREGISTERED');

            // 🚀 ATOM M-017: Clear location if last background map is removed
            if (removed.type === 'background' && !this.maps.some(m => m.type === 'background')) {
                this.currentLocation = null;
            }
        }
    }

    /**
     * Get all registered map instances with their types.
     */
    getMaps(): MapEntry[] {
        return this.maps;
    }

    /**
     * Clear all registered map instances.
     */
    clearMaps(): void {
        this.maps = [];
    }

    setCallbacks(callbacks: {
        onCommandExecuted?: (command: SpatialCommand) => void;
        onCommandRejected?: (command: SpatialCommand, reason: string) => void;
        onInteractionStateChanged?: (state: InteractionState) => void;
    }) {
        this.onCommandExecuted = callbacks.onCommandExecuted;
        this.onCommandRejected = callbacks.onCommandRejected;
        this.onInteractionStateChanged = callbacks.onInteractionStateChanged;
    }

    /**
     * Get current interaction state.
     */
    getInteractionState(): InteractionState {
        return { ...this.interactionState };
    }

    /**
     * 🛰️ ATOM S4: Unified State Sync Authority
     * This replaces the direct sync in SpatialBridge.jsx
     */
    public syncSpatialState(data: any) {
        this.traceReadiness();
        const { center, markers, zoom, bounds, selectedLocationId, essential } = data;

        // 🛰️ ATOM S6: Clear stale intelligence during flight stabilization
        contextState.setIntelligence({ weather: null, restaurants: [] });

        telemetry.forensic('SYNC', 'PIPELINE_INTAKE', {
            owner: 'SpatialCommandController',
            payload: { center, markers: markers?.length, zoom, hasBounds: !!bounds }
        });

        telemetry.trackPerf('intentThroughput', 1);

        // 1. Normalize Coordinates via Contract Utility
        const normalizedCenter = normalizeCoordinate(center, 'syncSpatialState');
        const currentCenter = this.maps[0]?.map ? toLngLatArray(this.maps[0].map.getCenter()) : null;

        const validation = validateSpatialIntent({
            center: normalizedCenter,
            bounds,
        }, currentCenter, 'SYNC');

        if (!validation.valid && validation.reason === 'invalid') {
            telemetry.warn('COMMAND_CONTROLLER', 'SYNC_VALIDATION_FAILED', { error: validation.error });
            return;
        }

        // 2. Execute Mapbox Movement
        const options = {
            center: normalizedCenter,
            zoom: zoom || 14,
            bounds,
            essential: essential !== false,
            intent: data.intent || 'navigate'
        };

        this.enqueueCommand({
            type: bounds ? 'FIT_BOUNDS' : 'FLY_TO',
            source: 'SYNC',
            params: options
        });

        // 3. 🎯 ATOM S4: Sync Three.js Camera (Orb)
        // Instead of the Three.js camera watching the store, we tell it where to look.
        spatialEventBus.emit('CAMERA_TARGET_UPDATE', {
            center: options.center,
            selectedLocationId,
            markers,
            essential
        });
    }

    // ============================================================================
    // INTERACTION STATE DETECTION (deck.gl pattern)
    // ============================================================================

    private initializeInteractionDetection() {
        if (typeof window === 'undefined') return;

        // Listen for map interaction events (Global window events for cross-bundle sync)
        window.addEventListener('mapbox:dragstart', () => this.setInteractionState({ isDragging: true }));
        window.addEventListener('mapbox:dragend', () => this.setInteractionState({ isDragging: false }));
        window.addEventListener('mapbox:zoomstart', () => this.setInteractionState({ isZooming: true }));
        window.addEventListener('mapbox:zoomend', () => this.setInteractionState({ isZooming: false }));

        window.addEventListener('mapbox:movestart', (e: any) => {
            // Only escalate to isPanning if it's a confirmed user movement
            if (e.detail?.isUser) {
                telemetry.throttledLog('INTERACTION_MOVESTART', {
                    subsystem: 'INTERACTION',
                    event: 'MOVESTART',
                    level: 'FORENSIC',
                    source: 'USER',
                    payload: { source: e.detail?.source || 'unknown' }
                }, 2000);
                this.setInteractionState({ isPanning: true });
            }
        });

        window.addEventListener('mapbox:moveend', (e: any) => {
            const state = this.getInteractionState();
            const isUser = e.detail?.isUser;

            // 🚀 ATOM W-009: Governance Gate for Ambient Drift
            // If the map is NOT animating via command, and the user didn't move it,
            // this is an ambient drift or cinematic orbit moveend tick. We must ignore it.
            if (!state.isAnimating && !state.isPanning && !isUser) {
                return;
            }

            telemetry.throttledLog('INTERACTION_MOVEEND', {
                subsystem: 'INTERACTION',
                event: 'MOVEEND',
                level: 'FORENSIC',
                payload: {
                    isUser: e.detail?.isUser,
                    source: e.detail?.source,
                    timestamp: Date.now()
                }
            }, 2000);
            this.setInteractionState({ isPanning: false, isAnimating: false });

            if (!isUser) {
                spatialStateMachine.onComplete();
                // 🛡️ ATOM W-006: Dispatch FlyTo end for shader governance
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('spatial:flyto_end'));
                }
            }
        });
    }

    private initializePaddingSync() {
        if (typeof window === 'undefined') return;

        spatialEventBus.on('SPATIAL_COMMAND', (payload: any) => {
            if (payload.action === 'PANEL_DETENT_CHANGE') {
                const { height, width, isMobile } = payload.params;
                const padding = isMobile
                    ? { top: 40, bottom: height + 40, left: 20, right: 20 }
                    : { top: 0, bottom: 0, left: 0, right: 0 };

                this.currentPadding = padding;
                this.applyPaddingToAllMaps();

                // Broadcast for any other listeners (e.g. debugging overlays)
                spatialEventBus.emit('VIEWPORT_RESIZE', { padding, isMobile });
            }
        });
    }

    private applyPaddingToAllMaps() {
        if (!this.currentPadding) return;

        // 🛡️ ONLY apply padding to background maps to prevent panel maps from being cut off
        const backgroundMaps = this.maps.filter(entry => entry.type === 'background');

        for (const entry of backgroundMaps) {
            try {
                if (typeof entry.map.setPadding === 'function') {
                    entry.map.setPadding(this.currentPadding);
                }
            } catch (err) {
                telemetry.error('COMMAND_CONTROLLER', 'PADDING_SYNC_FAILED', { err });
            }
        }
    }

    private setInteractionState(newState: Partial<InteractionState>) {
        const oldState = { ...this.interactionState };
        Object.assign(this.interactionState, newState);

        this.interactionState.inTransition =
            this.interactionState.isDragging ||
            this.interactionState.isZooming ||
            this.interactionState.isAnimating ||
            this.interactionState.isPanning;

        const isUserInteracting =
            this.interactionState.isDragging ||
            this.interactionState.isZooming ||
            this.interactionState.isPanning;

        if (isUserInteracting) {
            telemetry.throttledLog('USER_INTERACTION_DETECTED', {
                subsystem: 'INTERACTION',
                event: 'USER_ACTIVITY',
                level: 'FORENSIC',
                payload: {
                    dragging: this.interactionState.isDragging,
                    zooming: this.interactionState.isZooming,
                    panning: this.interactionState.isPanning
                }
            }, 5000);
            spatialStateMachine.onUserInteraction();
            if (this.userInteractionTimeout) {
                clearTimeout(this.userInteractionTimeout);
                this.userInteractionTimeout = null;
            }
        } else {
            if (this.userInteractionTimeout) clearTimeout(this.userInteractionTimeout);
            this.userInteractionTimeout = setTimeout(() => {
                if (!this.interactionState.inTransition) {
                    // 🛰️ ATOM S4: Trigger Intelligence upon stabilization
                    const map = this.maps.find(m => m.type === 'panel')?.map || this.maps[0]?.map;
                    if (map) {
                        const center = map.getCenter();
                        this.triggerIntelligence(center.lat, center.lng);
                    }

                    if (spatialStateMachine.getAuthority() === 'USER') {
                        telemetry.info('COMMAND_CONTROLLER', 'AUTHORITY_RELEASED', { reason: 'USER_COOLDOWN_FINISHED' });
                        spatialStateMachine.onMoveEnd();
                        this.onAuthorityReleased();
                    }
                }
            }, 3000);
        }

        if (this.onInteractionStateChanged) {
            this.onInteractionStateChanged(this.interactionState);
        }

        if (JSON.stringify(oldState) !== JSON.stringify(this.interactionState)) {
            telemetry.debug('COMMAND_CONTROLLER', 'INTERACTION_STATE_CHANGED', this.interactionState);
        }
    }
    // ============================================================================
    // COMMAND QUEUE MANAGEMENT
    // ============================================================================

    async enqueueCommand(command: Omit<SpatialCommand, 'id' | 'timestamp' | 'priority'>): Promise<CommandResult> {
        // 🔒 LOCATION LOCK GUARD
        if (this.isLocationLocked) {
            const allowedSources = ['USER', 'VOICE', 'GEMINI', 'UI', 'INDOOR_MAP'];
            const sourceUpper = (command.source || '').toUpperCase();

            if (!allowedSources.includes(sourceUpper)) {
                telemetry.log({
                    subsystem: 'COMMAND_CONTROLLER',
                    event: 'COMMAND_REJECTED',
                    level: 'INFO',
                    correlationId: command.correlationId,
                    payload: { reason: 'LOCATION_LOCKED', source: command.source }
                });
                return { success: false, reason: 'LOCATION_LOCKED' };
            }
        }

        const fullCommand: SpatialCommand = {
            ...command,
            id: this.generateCommandId(),
            timestamp: Date.now(),
            priority: COMMAND_PRIORITIES[command.source]
        };

        // 🛰️ COORDINATE CONTRACT ENFORCEMENT
        const normalizedCenter = normalizeCoordinate(fullCommand.params?.center, 'enqueueCommand');
        if (normalizedCenter) {
            fullCommand.params.center = normalizedCenter;
        }

        // 🛡️ RUNTIME GUARD: Location Integrity
        this.assertLocationIntegrity(fullCommand);

        // Rule 5: FIT_BOUNDS vs FLY_TO conflict
        if (fullCommand.type === 'FIT_BOUNDS' && fullCommand.source === 'AI') {
            const timeSinceLastFlyTo = Date.now() - this.lastFlyToTimestamp;
            if (timeSinceLastFlyTo < 2000) {
                telemetry.warn('COMMAND_CONTROLLER', 'FIT_BOUNDS_BLOCKED', { reason: 'FLY_TO_ACTIVE', timeSinceLastFlyTo });
                return { success: false, reason: 'COMMAND_BLOCKED — CONFLICT_WITH_ACTIVE_FLY', commandId: fullCommand.id };
            }
        }

        // Debounce AI commands
        if (this.shouldDebounceCommand(fullCommand)) {
            return { success: false, reason: 'DEBOUNCED', commandId: fullCommand.id };
        }

        if (fullCommand.type === 'FLY_TO' && fullCommand.source === 'AI') {
            this.lastFlyToTimestamp = Date.now();
        }
        const currentCenter = this.maps[0]?.map ? toLngLatArray(this.maps[0].map.getCenter()) : null;

        // 🛡️ AUTHORITY GATING (Centralized from validator)
        const currentAuthority = spatialStateMachine.getAuthority();
        const lockOwner = (spatialEventBus as any).getLockOwner?.();

        // 🚀 ATOM D1.4A: Respect EventBus locks as higher authority than state machine
        const effectiveAuthority = lockOwner || currentAuthority;
        const currentPriority = COMMAND_PRIORITIES[effectiveAuthority as CommandSource] || 0;
        let incomingPriority = fullCommand.priority;

        if (fullCommand.source === 'AI' && (fullCommand.params?.cinematicOverride || fullCommand.params?.essential)) {
            // 🚀 AI Escalation: Essential AI commands (Discovery) can override standard SYNC but not USER or TOUCH
            incomingPriority = (lockOwner === 'AI') ? 5.1 : 3.2;
        }

        // 🛡️ ATOM M-01: Reject if a higher priority lock (TOUCH) exists
        if (lockOwner === 'TOUCH' && fullCommand.source !== 'TOUCH') {
            return { success: false, reason: 'TOUCH_PRIORITY_LOCK' };
        }

        if (this.interactionState.isDragging || this.interactionState.isPanning) {
            if (fullCommand.source !== 'USER' && lockOwner !== fullCommand.source) {
                telemetry.info('COMMAND_CONTROLLER', 'COMMAND_QUEUED', { reason: 'USER_INTERACTION_ACTIVE', source: fullCommand.source });
                this.retryQueue.push(fullCommand);
                return { success: false, reason: 'RETRY_QUEUED', commandId: fullCommand.id };
            }
        }

        if (incomingPriority < currentPriority && lockOwner !== fullCommand.source) {
            telemetry.warn('COMMAND_CONTROLLER', 'AUTHORITY_CONFLICT', {
                incomingSource: fullCommand.source,
                incomingPriority,
                currentAuthority: effectiveAuthority,
                currentPriority
            }, fullCommand.correlationId);
            return { success: false, reason: 'AUTHORITY_BLOCKED', commandId: fullCommand.id };
        }

        // 🚀 ATOM D1.4C: Overlapping Easing Guard
        // Prevent concurrent animations from the same or lower priority source.
        if (this.interactionState.isAnimating && fullCommand.source !== 'USER' && lockOwner !== fullCommand.source) {
            if (incomingPriority <= currentPriority) {
                telemetry.info('COMMAND_CONTROLLER', 'COMMAND_BLOCKED', {
                    reason: 'ANIMATION_IN_PROGRESS',
                    source: fullCommand.source,
                    priority: incomingPriority
                });
                return { success: false, reason: 'ANIMATION_IN_PROGRESS', commandId: fullCommand.id };
            }
        }

        // 🔍 ATOM W-004: Zoom Operation Governance
        // Debounce rapid zoom button clicks to prevent command flooding.
        if (fullCommand.params?.zoom_delta !== undefined) {
            const now = Date.now();
            const lastTime = this.lastZoomEmission[fullCommand.source] || 0;
            if (now - lastTime < 100) {
                return { success: true, reason: 'DEDUPLICATED', commandId: fullCommand.id };
            }
            this.lastZoomEmission[fullCommand.source] = now;
        }

        const isZoomOnly = fullCommand.params?.zoom_delta !== undefined && !normalizedCenter && !fullCommand.params?.bounds;

        if (!isZoomOnly) {
            const validationResult = validateSpatialIntent({
                center: normalizedCenter,
                bounds: fullCommand.params?.bounds,
            }, currentCenter, fullCommand.source);

            if (!validationResult.valid) {
                if (validationResult.reason === 'invalid') {
                    telemetry.error('COMMAND_CONTROLLER', 'VALIDATION_FAILED', { error: validationResult.error }, fullCommand.correlationId);
                    return { success: false, reason: 'VALIDATION_FAILED', commandId: fullCommand.id };
                }
                // Jitter/Duplicate are soft-blocks, we just ignore them
                return { success: true, reason: 'DEDUPLICATED', commandId: fullCommand.id };
            }
        }

        await this.processCommand(fullCommand);
        return { success: true, commandId: fullCommand.id };
    }

    /**
     * 🛡️ ATOM S9.0.6: Unified Intent Intake
     * Translates standardized Intent Envelopes into executable Spatial Commands.
     */
    async enqueueIntent(intent: any): Promise<CommandResult> {
        telemetry.trackPerf('intentThroughput', 1);
        telemetry.forensic('INTENT', 'ROUTED', {
            from: intent.source,
            to: 'SpatialCommandController',
            pipeline: "authoritative"
        }, intent.intentId);

        let type: CommandType = 'FLY_TO';
        if (intent.intentType === 'FIT_BOUNDS') type = 'FIT_BOUNDS';
        if (intent.intentType === 'RESET_CAMERA') {
            // 🛡️ RECOVERY GOVERNANCE
            this.releaseLock('AI');
            shaderGovernance.restore();

            // 🎬 Interrupt any active discovery sequences
            if (typeof window !== 'undefined' && (window as any).DiscoveryExperienceOrchestrator) {
                (window as any).DiscoveryExperienceOrchestrator.endDiscoverySequence();
            }

            intent.payload = {
                center: [0, 20],
                zoom: 2.5,
                pitch: 0,
                bearing: 0
            };
        }
        if (intent.intentType === 'ZOOM_IN') {
            intent.payload = { zoom_delta: 1, essential: true };
        }
        if (intent.intentType === 'ZOOM_OUT') {
            intent.payload = { zoom_delta: -1, essential: true };
        }
        if (intent.intentType === 'DISCOVERY') {
            // 🚀 ATOM D1.0: Route to Orchestrator if available
            if (typeof window !== 'undefined' && (window as any).DiscoveryExperienceOrchestrator) {
                const p = intent.payload?.params || intent.payload;
                (window as any).DiscoveryExperienceOrchestrator.startDiscoverySequence({
                    name: p?.query?.replace('Fly to ', '') || p?.name || 'Destination',
                    center: p?.center,
                    zoom: p?.zoom,
                    pitch: p?.pitch,
                    bearing: p?.bearing
                });
                return { success: true, reason: 'DISCOVERY_ROUTED' };
            } else {
                telemetry.warn('COMMAND_CONTROLLER', 'DISCOVERY_ORCHESTRATOR_MISSING', { intent });
            }

            this.requestLock(intent.source);
            import('./utils/interactionForensics').then(({ interactionForensics }) => {
                interactionForensics.record({
                    stage: 'CONTROLLER',
                    type: intent.intentType,
                    source: intent.source,
                    payload: intent,
                    status: 'PROPAGATING'
                });
            });
            return { success: true, reason: 'INTENT_ACKNOWLEDGED' };
        }

        import('./utils/interactionForensics').then(({ interactionForensics }) => {
            interactionForensics.record({
                stage: 'CONTROLLER',
                type: intent.intentType,
                source: intent.source,
                payload: intent,
                status: 'PROPAGATING'
            });
        });

        return this.enqueueCommand({
            type,
            source: intent.source,
            params: intent.payload,
            correlationId: intent.intentId
        });
    }

    private requestLock(source: string) {
        if (typeof (spatialEventBus as any).requestLock === 'function') {
            (spatialEventBus as any).requestLock(source);
        }
    }

    private releaseLock(source: string) {
        if (typeof (spatialEventBus as any).releaseLock === 'function') {
            (spatialEventBus as any).releaseLock(source);
        }
    }

    private async processCommand(command: SpatialCommand) {
        this.traceReadiness();

        telemetry.throttledLog('COMMAND_PROCESS_INTAKE', {
            subsystem: 'COMMAND_PROCESS',
            event: 'INTAKE',
            level: 'FORENSIC',
            correlationId: command.correlationId,
            payload: {
                type: command.type,
                source: command.source,
                params: command.params
            }
        }, 1000);

        telemetry.trackPerf('queueDepth', this.commandQueue.length + this.retryQueue.length);

        if (this.isRedundant(command)) {
            telemetry.throttledLog(`REDUNDANT_${command.type}`, {
                subsystem: 'COMMAND_PROCESS',
                event: 'REDUNDANT',
                level: 'DEBUG',
                correlationId: command.correlationId,
                payload: { type: command.type }
            }, 5000);
            return { success: true, reason: 'REDUNDANT' };
        }

        const stateMachineAuthority = command.source === 'USER' ? 'USER' :
            command.source === 'VOICE' ? 'VOICE' :
                command.source === 'INDOOR_MAP' ? 'VOICE' :
                    command.source === 'COPILOT' ? 'COPILOT' : 'AI';

        telemetry.log({
            subsystem: 'COMMAND_PROCESS',
            event: 'STATE_SYNC',
            level: 'FORENSIC',
            correlationId: command.correlationId,
            payload: { stateMachineAuthority }
        });

        if (command.type === 'FLY_TO' || command.type === 'FIT_BOUNDS') {
            // Only escalate to 'AI' if the current mapping is 'AI' and it's essential
            const escalatedAuthority = (command.params?.essential && stateMachineAuthority === 'AI')
                ? 'AI'
                : stateMachineAuthority;
            spatialStateMachine.onAnimationStart(escalatedAuthority as any);

            // 🛡️ ATOM W-006: Dispatch FlyTo start for shader governance
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('spatial:flyto_start'));
            }
        }

        this.setInteractionState({ isAnimating: true });

        telemetry.log({
            subsystem: 'COMMAND_PROCESS',
            event: 'EXECUTE_START',
            level: 'FORENSIC',
            correlationId: command.correlationId,
            payload: { type: command.type }
        });
        const success = await this.executeCommand(command);
        telemetry.forensic('COMMAND_PROCESS', 'EXECUTE_END', { success, type: command.type }, command.correlationId);

        if (!success) {
            this.setInteractionState({ isAnimating: false });
            return { success: false, reason: 'EXECUTION_FAILED' };
        }

        return { success: true };
    }

    private isRedundant(command: any): boolean {
        if (this.maps.length === 0 || !command.params?.center) return false;

        const current = toLngLatObject(this.maps[0].map.getCenter());
        const target = toLngLatObject(command.params.center);

        if (!current || !target) return false;

        const distanceM = calculateDistanceHaversine(current, target);

        telemetry.log({
            subsystem: 'REDUNDANCY_CHECK',
            event: 'DISTANCE_CALCULATED',
            level: 'DEBUG',
            correlationId: command.correlationId,
            payload: {
                current,
                target,
                distanceM,
                isRedundant: distanceM < 0.1
            }
        });

        return distanceM < 0.1;
    }


    private async executeCommand(command: any): Promise<boolean> {
        console.log('[SPATIAL] Command dispatched');
        console.log('[SPATIAL] Action', command.type);
        console.log('[SPATIAL] Coordinates', command.params?.center || command.params?.bounds || null);

        // 🚀 ATOM SSoT: Push target location to Canonical Memory synchronously
        if (command.params?.center) {
            try {
                const norm = normalizeCoordinate(command.params.center);
                if (norm) {
                    contextState.setData({
                        ...contextState.getData(),
                        center: { lat: norm[1], lon: norm[0] },
                        zoom: command.params.zoom || contextState.getData().zoom || 14
                    });
                }
            } catch (e) {
                console.warn('[SPATIAL] Failed SSoT context update:', e);
            }
        }

        // 🚀 ADAPTER: EXECUTE ON ALL GLOBES (Consolidation Protocol)
        // We now execute directly on both background and panel maps.
        const targetMaps = this.maps;
        console.log('[SPATIAL] executeCommand executing on maps:', targetMaps.map(m => m.type));

        let authorityParams = command.params;

        for (const entry of targetMaps) {
            // 🛡️ RUNTIME IDENTITY CHECK: Prevent execution on zombie maps
            if (!entry.map) continue;

            try {
                const options: any = {
                    ...command.params,
                    intent: command.params?.intent || 'navigate',
                    essential: command.params?.essential !== false,
                };
                const decision = smartCameraMove(entry.map, options);
                if (decision && typeof decision !== 'string') {
                    authorityParams = { ...authorityParams, ...decision.params };
                }
            } catch (err) {
                telemetry.error('COMMAND_CONTROLLER', 'MAP_EXECUTION_FAILED', { err }, command.correlationId);
            }
        }

        // 🚀 ATOM D1.3: BROADCAST TO EVENT BUS AS PASSIVE SYNC
        // We emit SPATIAL_COMMAND instead of SPATIAL_INTENT so that:
        // 1. Passive subscribers (MiniMap) can jumpTo and shadow the movement.
        // 2. We avoid circular feedback loops with MapEngine's intent listeners.
        if (spatialEventBus) {
            // Find a decision from one of the maps to use as the authority
            // In a multi-map setup, they should be identical or very close.

            spatialEventBus.emit('SPATIAL_COMMAND', {
                action: command.type === 'FLY_TO' || command.type === 'FIT_BOUNDS' ? 'CAMERA_FLY_TO' : 'CAMERA_MOVE',
                params: {
                    ...authorityParams,
                    intent: command.params?.intent || 'navigate'
                }
            });
        }

        if (this.onCommandExecuted) {
            this.onCommandExecuted(command);
        }

        // 🛡️ ATOM S4: Animation state is now managed by global map events (moveend)
        // We set a safety timeout in case no map fires moveend
        if (this.safetyAnimationTimeout) {
            clearTimeout(this.safetyAnimationTimeout);
        }

        this.safetyAnimationTimeout = setTimeout(() => {
            if (this.interactionState.isAnimating) {
                telemetry.warn('COMMAND_CONTROLLER', 'ANIMATION_TIMEOUT', { reason: 'SAFETY_TIMEOUT_HIT' }, command.correlationId);
                this.setInteractionState({ isAnimating: false });
                spatialStateMachine.onComplete();
            }
            this.safetyAnimationTimeout = null;
        }, 10000);

        return true;
    }

    private onAuthorityReleased() {
        this.processRetryQueue();
    }

    private processRetryQueue() {
        if (this.retryQueue.length === 0) return;
        const next = this.retryQueue.shift();
        if (next) this.enqueueCommand(next);
    }

    private getAuthorityState(): string {
        if (this.interactionState.isDragging || this.interactionState.isZooming || this.interactionState.isPanning) {
            return 'USER_DRAGGING';
        }
        return spatialStateMachine.getAuthority();
    }

    private executeFallback() {
        const eventBus = typeof window !== 'undefined' ? (window as any).WingmanEventBus : null;
        if (eventBus) {
            eventBus.emit('SPATIAL_INTENT', { type: 'jump_to', center: [0, 0], zoom: 1 });
        }
    }

    private shouldDebounceCommand(command: SpatialCommand): boolean {
        if (command.source !== 'AI') return false;

        const center = normalizeCoordinate(command.params.center || (command.params.bounds ? this.resolveCenterFromBounds(command.params.bounds) : null), 'debounce');
        if (!center) return false;

        const now = Date.now();
        const timeDiff = now - this.lastCommand.timestamp;
        const dist = calculateDistanceHaversine(center, { lng: this.lastCommand.lon, lat: this.lastCommand.lat });

        if (dist < 10 && timeDiff < 800) {
            return true;
        }

        this.lastCommand = { lat: center[1], lon: center[0], timestamp: now };
        return false;
    }

    private shouldRejectCommand(command: SpatialCommand): boolean {
        if (command.source === 'USER') return false;

        if (command.source === 'SYNC') {
            if (this.interactionState.isDragging || this.interactionState.isZooming || this.interactionState.isPanning) {
                return true;
            }
            return false;
        }

        if (this.interactionState.isDragging || this.interactionState.isZooming) {
            return true;
        }

        const currentAuthority = spatialStateMachine.getAuthority();
        const currentPriority = COMMAND_PRIORITIES[currentAuthority as CommandSource] || 0;
        const incomingPriority = command.priority;

        if (spatialStateMachine.getState() === 'ANIMATING' && command.source === 'AI') {
            return false;
        }

        if (incomingPriority < currentPriority) {
            import('./utils/interactionForensics').then(({ interactionForensics }) => {
                interactionForensics.record({
                    stage: 'CONTROLLER',
                    type: command.type,
                    source: command.source,
                    payload: command,
                    status: 'BLOCKED',
                    reason: `PRIORITY_CONFLICT: ${currentAuthority} > ${command.source}`
                });
            });
            return true;
        }

        return false;
    }

    private getRejectionReason(command: SpatialCommand): string {
        if (this.interactionState.isDragging) return 'USER_DRAGGING';
        if (this.interactionState.isZooming) return 'USER_ZOOMING';

        const currentAuthority = spatialStateMachine.getAuthority();
        const currentPriority = COMMAND_PRIORITIES[currentAuthority as CommandSource] || 0;

        if (command.priority < currentPriority) {
            return `AUTHORITY_CONFLICT: ${currentAuthority} (${currentPriority}) > ${command.source} (${command.priority})`;
        }

        return 'UNKNOWN';
    }

    private generateCommandId(): string {
        return `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }


    private resolveCenterFromBounds(bounds: any): [number, number] | null {
        if (!bounds) return null;
        if (Array.isArray(bounds) && bounds.length === 2 &&
            Array.isArray(bounds[0]) && Array.isArray(bounds[1])) {
            return [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2];
        }
        return null;
    }

    private lastIntelligenceCenter: [number, number] | null = null;

    private async triggerIntelligence(lat: number, lon: number) {
        // Prevent API spam for small movements (approx 1-2 km)
        if (this.lastIntelligenceCenter) {
            const [lastLat, lastLon] = this.lastIntelligenceCenter;
            const diffLat = Math.abs(lat - lastLat);
            const diffLon = Math.abs(lon - lastLon);

            if (diffLat < 0.01 && diffLon < 0.01) {
                console.log("[CommandController] Skipping intelligence trigger; movement too small.");
                return;
            }
        }

        this.lastIntelligenceCenter = [lat, lon];
        telemetry.info('COMMAND_CONTROLLER', 'INTELLIGENCE_TRIGGERED', { lat, lon });

        // Fire-and-forget
        WeatherService.fetchAtPoint(lat, lon);

        const store = useWingmanStore.getState();
        const query = store?.locationQuery;

        if (query) {
            try {
                const venues = await PoiService.searchRestaurants(query);
                contextState.setIntelligence({ restaurants: venues });
            } catch (err) {
                telemetry.error('COMMAND_CONTROLLER', 'POI_FETCH_FAILED', { err });
            }
        }
    }

    private assertLocationIntegrity(command: SpatialCommand) {
        if (command.type === 'FLY_TO' || command.type === 'JUMP_TO') {
            const center = command.params?.center;
            const hasDelta = command.params?.zoom_delta !== undefined;
            if (!hasDelta && (!center || !Array.isArray(center) || center.length !== 2)) {
                throw new Error("🚫 Location integrity violation: Missing or invalid coordinates (and no zoom_delta provided)");
            }
        }
    }
}

// 🚀 SINGLETON INITIALIZATION
// 🚀 SINGLETON ENFORCEMENT
const _spatialCommandController = new SpatialCommandController();
const globalCommandController = (typeof window !== 'undefined' && (window as any).WingmanCommandController)
    ? (window as any).WingmanCommandController
    : _spatialCommandController;

if (typeof window !== 'undefined') {
    (window as any).WingmanCommandController = globalCommandController;
}

export { globalCommandController as spatialCommandController };

// Global Sync
if (typeof window !== 'undefined') {
    (window as any).WingmanCommandController = globalCommandController;

    // 🛡️ ATOM S4: Expose as the primary spatial control interface
    (window as any).WingmanSpatialControl = {
        syncSpatialState: (data: any) => globalCommandController.syncSpatialState(data),
        enqueueCommand: (command: any) => globalCommandController.enqueueCommand(command),
        enqueueIntent: (intent: any) => globalCommandController.enqueueIntent(intent),
        setLocationLock: (locked: boolean) => globalCommandController.setLocationLock(locked),
        getLocationLock: () => globalCommandController.getLocationLock(),
        setIntent: (intent: string) => {
            telemetry.info('COMMAND_CONTROLLER', 'INTENT_SYNC', { intent });
            spatialEventBus.emit('ORB_COMMAND', { action: 'SET_INTENT', intent });
        },
        speak: (message: string) => {
            return speechAuthority.speak(message, { source: 'USER_REQUESTED' });
        },
        cancelSpeech: () => {
            speechAuthority.cancel();
        }
    };
}

export default globalCommandController;
